<script setup lang="ts" generic="T extends string = string">
import { ChevronDown } from '@lucide/vue'

/**
 * PSelect — liste déroulante du design system PreLLM.
 *
 * Enrobe un `<select>` natif (accessibilité + clavier gratuits) avec le chrome
 * Revelio : bordure line, focus border ink-3, chevron lucide custom. Remplace
 * les `<select class="p-select">` bruts.
 *
 * Deux façons de fournir les options :
 *   - prop `:options="[{value,label}]"` (pratique) ;
 *   - slot par défaut de `<option>` (drop-in des selects existants).
 * `placeholder` ajoute une option vide non sélectionnable en tête.
 *
 * v-model standard (string). Label optionnel (mono uppercase, comme PInput) :
 * sans label, le composant est un contrôle nu utilisable en ligne (builders).
 */
interface Option {
    value: T
    label: string
    disabled?: boolean
}

const props = withDefaults(
    defineProps<{
        modelValue?: T
        options?: Option[]
        label?: string
        placeholder?: string
        size?: 'sm' | 'md' | 'lg'
        disabled?: boolean
        required?: boolean
        error?: string
        hint?: string
        id?: string
        /** Libellé accessible quand il n'y a pas de `label` visible. */
        ariaLabel?: string
    }>(),
    {
        size: 'md',
        disabled: false,
        required: false,
    },
)

const emit = defineEmits<{
    'update:modelValue': [value: T]
    change: [value: T]
}>()

function onChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value as T
    emit('update:modelValue', value)
    emit('change', value)
}

const selectId = props.id ?? `p-select-${crypto.randomUUID().slice(0, 8)}`
</script>

<template>
    <div :class="['p-select-field', `p-select-field--${size}`, { 'p-select-field--error': error }]">
        <label v-if="label" :for="selectId" class="p-select-field__label">
            {{ label }}
            <span v-if="required" aria-hidden="true" class="p-select-field__required">*</span>
        </label>
        <div class="p-select-field__control">
            <select
                :id="selectId"
                class="p-select-field__native"
                :value="modelValue"
                :disabled="disabled"
                :required="required"
                :aria-label="ariaLabel"
                :aria-invalid="error ? 'true' : 'false'"
                @change="onChange"
            >
                <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
                <template v-if="options">
                    <option
                        v-for="opt in options"
                        :key="opt.value"
                        :value="opt.value"
                        :disabled="opt.disabled"
                    >
                        {{ opt.label }}
                    </option>
                </template>
                <slot v-else />
            </select>
            <ChevronDown class="p-select-field__chevron" :size="16" :stroke-width="1.5" />
        </div>
        <p v-if="error" class="p-select-field__error">{{ error }}</p>
        <p v-else-if="hint" class="p-select-field__hint">{{ hint }}</p>
    </div>
</template>

<style scoped>
.p-select-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.p-select-field__label {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    font-weight: var(--p-font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
}

.p-select-field__required {
    color: var(--p-color-brick);
    margin-left: 4px;
}

.p-select-field__control {
    position: relative;
    display: flex;
    align-items: center;
}

.p-select-field__native {
    appearance: none;
    -webkit-appearance: none;
    width: 100%;
    background: var(--p-color-bg);
    color: var(--p-color-ink);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    font-family: var(--p-font-sans);
    cursor: pointer;
    transition: border-color var(--p-motion-fast) var(--p-ease-out);
}

.p-select-field__native:hover:not(:disabled) {
    border-color: var(--p-color-ink-6);
}

.p-select-field__native:focus-visible {
    outline: none;
    border-color: var(--p-color-ink-3);
}

.p-select-field__native:disabled {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink-5);
    cursor: not-allowed;
}

.p-select-field__chevron {
    position: absolute;
    right: 10px;
    color: var(--p-color-ink-4);
    pointer-events: none;
}

.p-select-field--error .p-select-field__native {
    border-color: var(--p-color-brick);
}

/* Tailles : padding aligné sur PInput, réserve à droite pour le chevron. */
.p-select-field--sm .p-select-field__native {
    padding: 6px 30px 6px 10px;
    font-size: var(--p-text-xs);
}
.p-select-field--md .p-select-field__native {
    padding: 8px 32px 8px 12px;
    font-size: var(--p-text-sm);
}
.p-select-field--lg .p-select-field__native {
    padding: 10px 34px 10px 14px;
    font-size: var(--p-text-base);
}

.p-select-field__error {
    margin: 0;
    color: var(--p-color-brick);
    font-size: var(--p-text-xs);
    line-height: var(--p-line-relaxed);
}

.p-select-field__hint {
    margin: 0;
    color: var(--p-color-ink-4);
    font-size: var(--p-text-xs);
    line-height: var(--p-line-relaxed);
}
</style>
